import React, { useMemo } from 'react';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { TextareaInput } from '@components/atoms/TextareaInput';
import ReactSelect from '@components/atoms/ReactSelect';
import { GroupedSelect } from '@components/atoms/ReactSelect/GroupedSelect';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { DiscountToggle } from '@components/molecules/DiscountToggle';
import { BudgetLineItem, LineItemDiscountType } from '@models/lineItem.model';
import {
  ImperialWeightOptions,
  MetricWeightOptions,
  WeightOptions,
} from '@utils/options';

const convertOptions = (
  options: { id: string; name: string; group?: string }[],
) =>
  options.map((option) => ({
    value: option.id,
    label: option.name,
    group: option.group,
  }));

const systemOptions = convertOptions(WeightOptions);
const metricUnitOptions = convertOptions(MetricWeightOptions);
const imperialUnitOptions = convertOptions(ImperialWeightOptions);

interface LineItemSubmitValues {
  description: string;
  quantity: number;
  unitOfMeasure: string;
  amountSystem: string;
  unitPrice: number;
  salePricePerUnit: number | null;
  chargeToClient: boolean;
  discountType: LineItemDiscountType | null;
  discountValue: number;
  notes: string;
}

interface LineItemFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineItem?: BudgetLineItem | null;
  onSubmit: (values: LineItemSubmitValues) => void;
}

const validationSchema = Yup.object().shape({
  description: Yup.string().required('Description is required'),
  quantity: Yup.number()
    .required('Quantity is required')
    .moreThan(0, 'Quantity must be greater than 0'),
  amountSystem: Yup.string().required('System is required'),
  unitOfMeasure: Yup.string().required('Unit is required'),
  unitPrice: Yup.number()
    .required('Cost per unit is required')
    .moreThan(0, 'Cost per unit must be greater than 0'),
  salePricePerUnit: Yup.number().nullable(),
  discountType: Yup.mixed<LineItemDiscountType>()
    .oneOf(['USD', 'PERCENT'])
    .nullable(),
  discountValue: Yup.number()
    .min(0, 'Discount cannot be negative')
    .when('discountType', {
      is: 'PERCENT',
      then: (schema) => schema.max(100, 'Percent discount cannot exceed 100'),
    }),
});

const formatCurrency = (value: number): string =>
  `$ ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

export const LineItemFormModal: React.FC<LineItemFormModalProps> = ({
  isOpen,
  onClose,
  lineItem,
  onSubmit,
}) => {
  const isEditing = !!lineItem;

  const initialValues = useMemo(
    () => ({
      description: lineItem?.description || '',
      quantity: lineItem?.quantity ?? 1,
      amountSystem: lineItem?.amountSystem || 'imperial',
      unitOfMeasure: lineItem?.unitOfMeasure || '',
      unitPrice: lineItem?.unitPrice ?? 0,
      salePricePerUnit: lineItem?.salePricePerUnit ?? 0,
      discountType: (lineItem?.discountType ?? null) as LineItemDiscountType | null,
      discountValue: lineItem?.discountValue ?? 0,
      notes: lineItem?.notes ?? '',
    }),
    [lineItem],
  );

  const handleSubmit = (
    values: typeof initialValues,
    { setSubmitting }: FormikHelpers<typeof initialValues>,
  ) => {
    // chargeToClient is auto-derived: if there's a sale price, the item gets billed
    // to the client (and therefore shows up pre-selected in the invoice picker).
    const sale = Number(values.salePricePerUnit) > 0 ? Number(values.salePricePerUnit) : null;
    // A discount only counts when a type is selected AND value is positive — otherwise
    // collapse to "no discount" so the backend stores (null, 0) instead of mismatched pairs.
    const numericDiscount = Number(values.discountValue || 0);
    const discountType: LineItemDiscountType | null =
      values.discountType && numericDiscount > 0 ? values.discountType : null;
    const discountValue = discountType ? numericDiscount : 0;
    onSubmit({
      description: values.description,
      quantity: Number(values.quantity),
      unitOfMeasure: values.unitOfMeasure,
      amountSystem: values.amountSystem,
      unitPrice: Number(values.unitPrice),
      salePricePerUnit: sale,
      chargeToClient: sale !== null,
      discountType,
      discountValue,
      notes: values.notes.trim(),
    });
    setSubmitting(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Line Item' : 'Add Line Item'}
      size="small">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize>
        {({ values, errors, touched, setFieldValue, isSubmitting }) => {
          const unitOptions =
            values.amountSystem === 'metric' ? metricUnitOptions : imperialUnitOptions;
          const computedCost = round2(Number(values.quantity || 0) * Number(values.unitPrice || 0));
          const computedSale =
            Number(values.salePricePerUnit || 0) > 0
              ? round2(Number(values.quantity || 0) * Number(values.salePricePerUnit || 0))
              : null;

          return (
            <Form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <Field
                  as={Input}
                  name="description"
                  placeholder="e.g. Building permit, Cleanup labor"
                />
                {errors.description && touched.description && (
                  <p className="text-xs text-red-500 mt-1">{errors.description}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    System *
                  </label>
                  <ReactSelect
                    name="amountSystem"
                    options={systemOptions}
                    value={systemOptions.find((o) => o.value === values.amountSystem) || null}
                    onChange={(option: any) => {
                      setFieldValue('amountSystem', option?.value || 'imperial');
                      setFieldValue('unitOfMeasure', '');
                    }}
                    placeholder="Imperial / Metric"
                    error={!!(errors.amountSystem && touched.amountSystem)}
                  />
                  {errors.amountSystem && touched.amountSystem && (
                    <p className="text-xs text-red-500 mt-1">{errors.amountSystem}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit *
                  </label>
                  <ReactSelect
                    name="unitOfMeasure"
                    options={unitOptions}
                    value={unitOptions.find((o) => o.value === values.unitOfMeasure) || null}
                    onChange={(option: any) => {
                      setFieldValue('unitOfMeasure', option?.value || '');
                    }}
                    placeholder="Unit"
                    customComponent={GroupedSelect}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    error={!!(errors.unitOfMeasure && touched.unitOfMeasure)}
                  />
                  {errors.unitOfMeasure && touched.unitOfMeasure && (
                    <p className="text-xs text-red-500 mt-1">{errors.unitOfMeasure}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity *
                  </label>
                  <Field
                    as={Input}
                    name="quantity"
                    type="number"
                    step="0.0001"
                    min={0}
                  />
                  {errors.quantity && touched.quantity && (
                    <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost per unit *
                  </label>
                  <InputCurrency
                    value={values.unitPrice}
                    onChange={(val) => setFieldValue('unitPrice', val)}
                    placeholder="0.00"
                  />
                  {errors.unitPrice && touched.unitPrice && (
                    <p className="text-xs text-red-500 mt-1">{errors.unitPrice}</p>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 flex items-center justify-between">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Total cost</span>
                <span className="text-base font-semibold text-gray-900">
                  {formatCurrency(computedCost)}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price charged to client
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  Leave empty if this item is internal and won't be billed.
                </p>
                <InputCurrency
                  value={values.salePricePerUnit}
                  onChange={(val) => setFieldValue('salePricePerUnit', val)}
                  placeholder="0.00"
                />
                {computedSale !== null && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mt-2 flex items-center justify-between">
                    <span className="text-xs text-emerald-700 uppercase tracking-wide">
                      Total charged
                    </span>
                    <span className="text-base font-semibold text-emerald-900">
                      {formatCurrency(computedSale)}
                    </span>
                  </div>
                )}

                <DiscountToggle
                  className="mt-4"
                  helper="Optional. Applied to the price charged per unit before the line lands on an invoice."
                  unitPrice={Number(values.salePricePerUnit || 0)}
                  quantity={Number(values.quantity || 0)}
                  value={{ type: values.discountType, value: Number(values.discountValue || 0) }}
                  onChange={(next) => {
                    setFieldValue('discountType', next.type);
                    setFieldValue('discountValue', next.value);
                  }}
                  error={touched.discountValue ? (errors.discountValue as string | undefined) : undefined}
                />
                {computedSale !== null && values.unitPrice > 0 && (
                  <p
                    className={`text-xs mt-2 font-medium ${
                      values.salePricePerUnit >= values.unitPrice
                        ? 'text-green-600'
                        : 'text-yellow-600'
                    }`}>
                    Profit per unit: ${(values.salePricePerUnit - values.unitPrice).toFixed(2)}
                    {' · '}
                    Total profit: ${(computedSale - computedCost).toFixed(2)}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  Optional. Internal context or scope detail for this line item.
                </p>
                <TextareaInput
                  name="notes"
                  rows={3}
                  value={values.notes}
                  onChange={(e) => setFieldValue('notes', e.target.value)}
                  placeholder="e.g. Includes permit fee, client to confirm finish"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {isEditing ? 'Update Line Item' : 'Save Line Item'}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
